import React, { useState } from "react";
import { Select, Alert, Modal, Radio, Tooltip, Button } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import {
  STOP_ORDER_MODES,
  STOP_ORDER_MODE_OPTIONS,
  DEFAULT_STOP_ORDER_MODE,
  OSRM_TRIP_MAX_STOPS_PER_ROUTE
} from "../stopOrderModes";

import "./StopOrderingSelect.scss";

const InfoTip = ({ text }) => (
  <Tooltip title={text} overlayClassName="stop-ordering-tooltip">
    <InfoCircleOutlined
      className="stop-ordering-info"
      // Inside a dropdown option or a radio label, a hover on the icon must not double as a
      // click on the row it sits in.
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    />
  </Tooltip>
);

/**
 * Lets the user choose how their stop list becomes routes, at the confirm-locations step —
 * before any optimization runs. Shown for manual orders too, not just imports.
 *
 * The choice is asked for twice: a modal opens on arrival so the decision is made
 * deliberately rather than defaulted into, and the inline select stays available for
 * changing it afterwards. Dismissing the modal (mask, Esc or Cancel) is itself an answer —
 * it selects DEFAULT_STOP_ORDER_MODE.
 *
 * Rendered by both the client wizard (StepContent, Step 2) and the admin wizard
 * (AdminCreateOrderPage, Step 3).
 */
const StopOrderingSelect = ({
  stopOrderMode = DEFAULT_STOP_ORDER_MODE,
  setStopOrderMode,
  dropoffs = [],
  preferredNoOfStops
}) => {
  const [pickerOpen, setPickerOpen] = useState(true);
  const [pendingMode, setPendingMode] = useState(stopOrderMode);

  // How many stops the largest route would hold if we grouped now: by the import's
  // "Prerouted Route ID" column when every stop has one, else by the usual chunking.
  const groupKeys = dropoffs.map((dropoff) => dropoff?.preroutedRouteId);
  const hasRouteGroups = groupKeys.length > 0 && groupKeys.every(Boolean);
  const largestRoute = hasRouteGroups
    ? Math.max(
        ...[...new Set(groupKeys)].map((key) => groupKeys.filter((k) => k === key).length)
      )
    : Math.min(dropoffs.length, Number(preferredNoOfStops) || 15);

  const tripServiceTooBig = largestRoute > OSRM_TRIP_MAX_STOPS_PER_ROUTE;

  const isDisabled = (option) =>
    option.disabled ||
    (option.value === STOP_ORDER_MODES.OSRM_OPTIMIZE && tripServiceTooBig);

  const dismissPicker = () => {
    setPendingMode(DEFAULT_STOP_ORDER_MODE);
    setStopOrderMode(DEFAULT_STOP_ORDER_MODE);
    setPickerOpen(false);
  };

  const confirmPicker = () => {
    setStopOrderMode(pendingMode);
    setPickerOpen(false);
  };

  return (
    <div className="stop-ordering">
      <p className="location-title">Stop Ordering</p>
      <Select
        className="stop-ordering-select"
        style={{ width: "100%", maxWidth: 520 }}
        value={stopOrderMode}
        onChange={setStopOrderMode}
        optionLabelProp="label"
      >
        {STOP_ORDER_MODE_OPTIONS.map((option) => (
          <Select.Option
            key={option.value}
            value={option.value}
            label={option.label}
            disabled={isDisabled(option)}
          >
            <span className="stop-ordering-option">
              {option.label}
              <InfoTip text={option.info} />
            </span>
          </Select.Option>
        ))}
      </Select>

      {!hasRouteGroups && stopOrderMode !== STOP_ORDER_MODES.OPTIMIZE && (
        <Alert
          className="mt-2"
          type="info"
          showIcon
          message={
            'No "Prerouted Route ID" column was found, so stops keep the order they were ' +
            "entered in and are split into routes by your preferred stops per route."
          }
        />
      )}

      {tripServiceTooBig && (
        <Alert
          className="mt-2"
          type="warning"
          showIcon
          message={`"OSRM Routing" is unavailable because one route would have ${largestRoute} stops, above the ${OSRM_TRIP_MAX_STOPS_PER_ROUTE}-stop limit of OSRM's trip service. Split the stops into smaller routes to use it.`}
        />
      )}

      <Modal
        title="Stop Ordering"
        className="stop-ordering-modal"
        visible={pickerOpen}
        onCancel={dismissPicker}
        maskClosable
        keyboard
        footer={[
          <Button key="cancel" onClick={dismissPicker}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={confirmPicker}>
            Continue
          </Button>
        ]}
      >
        <p className="stop-ordering-modal-intro">
          Choose how your stops become routes. Dismissing this keeps the default,{" "}
          {
            STOP_ORDER_MODE_OPTIONS.find(
              (option) => option.value === DEFAULT_STOP_ORDER_MODE
            )?.label
          }
          .
        </p>
        <Radio.Group
          className="stop-ordering-modal-options"
          value={pendingMode}
          onChange={(event) => setPendingMode(event.target.value)}
        >
          {STOP_ORDER_MODE_OPTIONS.map((option) => (
            <Radio key={option.value} value={option.value} disabled={isDisabled(option)}>
              <span className="stop-ordering-option">
                {option.label}
                <InfoTip text={option.info} />
              </span>
            </Radio>
          ))}
        </Radio.Group>
      </Modal>
    </div>
  );
};

export default StopOrderingSelect;
